import React, { useState, useRef, useEffect } from 'react';
import { authPost } from '../../lib/authFetch';
import {
  isLocalModeEnabled, setLocalModeEnabled,
  getLocalDonors, getLocalGroups, getLocalLists, getLocalTables,
  saveLocalDonors, saveLocalGroups, saveLocalLists, saveLocalTables
} from '../../lib/localMode';
import './DesktopSettings.css';

function DesktopSettings({ user, subscription, onSignOut, onClose, userContext, onSaveUserContext, donors, groups, onImportPeople }) {
  const [isExporting, setIsExporting] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [localMode, setLocalMode] = useState(() => isLocalModeEnabled());

  // User context modal state
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [editingContext, setEditingContext] = useState('');
  const [isSavingContext, setIsSavingContext] = useState(false);
  const contextInputRef = useRef(null);

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  // Local Mode backup/restore
  const [isRestoring, setIsRestoring] = useState(false);
  const restoreFileInputRef = useRef(null);

  // Initialize editing context when modal opens
  useEffect(() => {
    if (contextModalOpen) {
      setEditingContext(userContext || '');
      setTimeout(() => contextInputRef.current?.focus(), 100);
    }
  }, [contextModalOpen, userContext]);

  // Get subscription status
  const getSubscriptionStatus = () => {
    if (!subscription) return { label: 'Free', color: '#888' };
    if (subscription.plan === 'trial') return { label: 'Trial', color: '#fbbf24' };
    if (subscription.plan === 'pro') return { label: 'Pro', color: '#4ade80' };
    if (subscription.status === 'canceled') return { label: 'Canceled', color: '#ef4444' };
    return { label: 'Free', color: '#888' };
  };

  const subscriptionStatus = getSubscriptionStatus();

  // Handle export - open modal
  const handleExport = () => {
    setExportModalOpen(true);
  };

  // Perform the actual export
  const performExport = () => {
    setIsExporting(true);
    try {
      if (exportFormat === 'localBackup') {
        // Exact snapshot of everything in local storage - unlike the CSV/JSON
        // options below, nothing here is flattened or reformatted, so importing
        // it back restores donors, groups, lists, and tables exactly as they were.
        const backupData = {
          exportedAt: new Date().toISOString(),
          version: 1,
          donors: getLocalDonors(),
          groups: getLocalGroups(),
          lists: getLocalLists(),
          tables: getLocalTables()
        };
        const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `knowroom-backup-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
        setExportModalOpen(false);
        setIsExporting(false);
        return;
      }

      const donorList = Object.entries(donors || {}).map(([name, data]) => {
        const group = groups?.find(g => g.id === data.group_id || g.id === data.groupId);
        return {
          name,
          group: group?.name || '',
          groupContext: group?.context || '',
          narrative: data.narrative || '',
          keyDetails: Array.isArray(data.keyDetails) ? data.keyDetails.join('; ') : (data.keyDetails || ''),
          tasks: Array.isArray(data.tasks) ? data.tasks.join('; ') : (data.tasks || ''),
          lastContact: data.lastContact || '',
          amount: data.amount || '',
          stage: data.stage || '',
          timeline: Array.isArray(data.timeline)
            ? data.timeline.map(t => `${t.date || ''}: ${t.event || t.text || ''}`).join(' | ')
            : ''
        };
      });

      if (exportFormat === 'csv') {
        // CSV Export
        const headers = ['Name', 'Group', 'Notes', 'Key Details', 'Tasks', 'Last Contact', 'Amount', 'Stage'];
        const rows = donorList.map(d => [
          d.name,
          d.group,
          d.narrative.replace(/"/g, '""'), // Escape quotes
          d.keyDetails,
          d.tasks,
          d.lastContact,
          d.amount,
          d.stage
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `knowroom-export-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // JSON Export - full data
        const exportData = {
          exportedAt: new Date().toISOString(),
          people: donorList,
          groups: (groups || []).map(g => ({
            name: g.name,
            context: g.context || '',
            color: g.color || ''
          }))
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `knowroom-export-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
      }

      setExportModalOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  // Restore a Local Mode backup file - replaces everything currently in local
  // storage, so confirm first since it's not reversible without another backup.
  const handleRestoreClick = () => {
    restoreFileInputRef.current?.click();
  };

  const handleRestoreFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!window.confirm('This will replace everything currently stored in this browser with the contents of the backup file. Continue?')) {
      return;
    }

    setIsRestoring(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data || typeof data !== 'object') {
          throw new Error('Not a valid backup file');
        }
        saveLocalDonors(Array.isArray(data.donors) ? data.donors : []);
        saveLocalGroups(Array.isArray(data.groups) ? data.groups : []);
        saveLocalLists(Array.isArray(data.lists) ? data.lists : []);
        saveLocalTables(Array.isArray(data.tables) ? data.tables : []);
        // Reload so every part of the app picks up the restored data, same as
        // toggling Local Mode does.
        window.location.reload();
      } catch (err) {
        console.error('Restore failed:', err);
        alert('Restore failed: ' + err.message);
        setIsRestoring(false);
      }
    };
    reader.onerror = () => {
      alert('Failed to read backup file.');
      setIsRestoring(false);
    };
    reader.readAsText(file);
  };

  // Handle cancel subscription
  const handleCancelSubscription = () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to Pro features at the end of your billing period.')) {
      // TODO: Implement actual cancellation
      alert('Please contact support@knowroom.com to cancel your subscription.');
    }
  };

  // Handle upgrade to Pro
  const handleUpgradeToPro = async () => {
    setIsUpgrading(true);
    try {
      const response = await authPost('/api/create-checkout-session', {});
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
      alert('Failed to start checkout. Please try again.');
    }
    setIsUpgrading(false);
  };

  // Handle save user context
  const handleSaveContext = async () => {
    setIsSavingContext(true);
    try {
      await onSaveUserContext?.(editingContext);
      setContextModalOpen(false);
    } catch (error) {
      console.error('Failed to save context:', error);
    } finally {
      setIsSavingContext(false);
    }
  };

  return (
    <div className="desktop-settings">
      <div className="settings-content">
        {/* Header */}
        <div className="settings-header">
          <h1>Settings</h1>
          {onClose && (
            <button className="settings-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Account Section */}
        <div className="settings-section">
          <h2>Account</h2>

          <div className="settings-row">
            <div className="settings-row-label">Email</div>
            <div className="settings-row-value">{user?.email || 'Not signed in'}</div>
          </div>

          <div className="settings-row">
            <div className="settings-row-label">Status</div>
            <div className="settings-row-value">
              <span className="status-badge" style={{ background: subscriptionStatus.color }}>
                {subscriptionStatus.label}
              </span>
            </div>
          </div>

          {subscription?.status === 'trialing' && subscription?.trial_end && (
            <div className="settings-row">
              <div className="settings-row-label">Trial ends</div>
              <div className="settings-row-value">
                {new Date(subscription.trial_end).toLocaleDateString()}
              </div>
            </div>
          )}

          {/* Upgrade button for trial/free users */}
          {(subscription?.plan === 'trial' || subscription?.plan === 'free' || !subscription?.plan) && (
            <button
              className="settings-btn upgrade"
              onClick={handleUpgradeToPro}
              disabled={isUpgrading}
            >
              {isUpgrading ? 'Loading...' : 'Upgrade to Pro'}
            </button>
          )}

          {subscription?.status === 'active' && subscription?.plan === 'pro' && (
            <button className="settings-btn danger" onClick={handleCancelSubscription}>
              Cancel Subscription
            </button>
          )}
        </div>

        {/* User Context Section */}
        <div className="settings-section">
          <h2>Your Context</h2>
          <p className="settings-description">
            Help the AI understand how you use Knowroom to get more personalized responses.
          </p>

          <div className="settings-row">
            <div className="settings-row-label">Current context</div>
            <div className="settings-row-value context-preview">
              {userContext ? (
                <span className="context-text">{userContext.substring(0, 60)}{userContext.length > 60 ? '...' : ''}</span>
              ) : (
                <span className="context-empty">Not set</span>
              )}
            </div>
          </div>

          <button className="settings-btn" onClick={() => setContextModalOpen(true)}>
            {userContext ? 'Edit Context' : 'Set Context'}
          </button>
        </div>

        {/* Data Section */}
        <div className="settings-section">
          <h2>Data</h2>

          <div className="settings-actions">
            <button
              className="settings-btn"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export Data'}
            </button>

            {onImportPeople && (
              <button className="settings-btn" onClick={onImportPeople}>
                Import Contacts
              </button>
            )}
          </div>
          <p className="settings-description" style={{ marginTop: '8px' }}>
            Import from CSV or JSON exports.
          </p>
        </div>

        {/* Local Storage Mode */}
        <div className="settings-section">
          <h2>Local Storage Mode</h2>
          <div className="settings-row">
            <div className="settings-row-label">Keep new people on this device</div>
            <button
              type="button"
              role="switch"
              aria-checked={localMode}
              className={`settings-toggle ${localMode ? 'on' : ''}`}
              onClick={() => {
                const next = !localMode;
                setLocalModeEnabled(next);
                setLocalMode(next);
                // All app data (donors, groups, lists, tables) is only loaded once on
                // mount from whichever source was active then - reload so it's
                // re-fetched from the newly-selected source instead of showing stale data.
                window.location.reload();
              }}
            >
              <span className="settings-toggle-thumb" />
            </button>
          </div>
          <p className="settings-description">
            {localMode
              ? "On — new people you add or import will be saved only in this browser, not to your account. You can turn this off and migrate them to your account later."
              : "Off — Knowroom works as it does today, saving everything to your account."}
          </p>
          {localMode && (
            <>
              <button
                className="settings-btn secondary"
                onClick={handleRestoreClick}
                disabled={isRestoring}
                style={{ marginTop: '12px' }}
              >
                {isRestoring ? 'Restoring...' : 'Restore from Backup'}
              </button>
              <input
                type="file"
                accept="application/json"
                ref={restoreFileInputRef}
                onChange={handleRestoreFile}
                style={{ display: 'none' }}
              />
              <p className="settings-description" style={{ marginTop: '8px' }}>
                Since this data lives only in this browser, it's worth exporting a full backup
                (Export Data → Full Backup) somewhere safe, and restoring it here if this browser's
                storage ever gets cleared.
              </p>
            </>
          )}
        </div>

        {/* Sign Out */}
        <div className="settings-section">
          <button className="settings-btn signout" onClick={onSignOut}>
            Sign Out
          </button>
        </div>

        {/* Footer */}
        <div className="settings-footer">
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <span className="footer-divider">•</span>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
        </div>
      </div>

      {/* User Context Modal */}
      {contextModalOpen && (
        <div className="settings-modal-overlay" onClick={() => setContextModalOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Edit Your Context</h3>
              <button className="settings-modal-close" onClick={() => setContextModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="settings-modal-body">
              <p className="settings-modal-description">
                Describe how you use Knowroom. This helps the AI tailor its responses to your needs.
              </p>
              <textarea
                ref={contextInputRef}
                className="settings-modal-textarea"
                value={editingContext}
                onChange={(e) => setEditingContext(e.target.value)}
                placeholder="e.g., I use Knowroom for fundraising - managing donors, tracking donations, and building relationships with supporters."
                rows={4}
              />
            </div>
            <div className="settings-modal-footer">
              <button
                className="settings-btn secondary"
                onClick={() => setContextModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="settings-btn primary"
                onClick={handleSaveContext}
                disabled={isSavingContext}
              >
                {isSavingContext ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="settings-modal-overlay" onClick={() => setExportModalOpen(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h3>Export Data</h3>
              <button className="settings-modal-close" onClick={() => setExportModalOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="settings-modal-body">
              <p className="settings-modal-description">
                Export your contacts and groups. Choose a format below.
              </p>

              <div className="export-format-options">
                <label className={`export-format-option ${exportFormat === 'csv' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="exportFormat"
                    value="csv"
                    checked={exportFormat === 'csv'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  <div className="export-format-content">
                    <span className="export-format-title">CSV</span>
                    <span className="export-format-desc">Opens in Excel, Google Sheets</span>
                  </div>
                </label>

                <label className={`export-format-option ${exportFormat === 'json' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="exportFormat"
                    value="json"
                    checked={exportFormat === 'json'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  <div className="export-format-content">
                    <span className="export-format-title">JSON</span>
                    <span className="export-format-desc">Readable snapshot of your people and groups</span>
                  </div>
                </label>

                {localMode && (
                  <label className={`export-format-option ${exportFormat === 'localBackup' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="localBackup"
                      checked={exportFormat === 'localBackup'}
                      onChange={(e) => setExportFormat(e.target.value)}
                    />
                    <div className="export-format-content">
                      <span className="export-format-title">Full Backup</span>
                      <span className="export-format-desc">Exact copy for restoring this browser's data later</span>
                    </div>
                  </label>
                )}
              </div>

              <div className="export-summary">
                <span className="export-summary-count">{Object.keys(donors || {}).length} people</span>
                <span className="export-summary-divider">•</span>
                <span className="export-summary-count">{(groups || []).length} groups</span>
              </div>
            </div>
            <div className="settings-modal-footer">
              <button
                className="settings-btn secondary"
                onClick={() => setExportModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="settings-btn primary"
                onClick={performExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DesktopSettings;
